import { Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';

import { ReactiveComponent } from 'src/app/components/shared/reactive-component';
import { GeneralHelper } from 'src/app/helpers';
import { GatewayTransactionModalView } from 'src/app/models';
import {
  CancelGatewayTransaction,
  CancelGatewayTransactionSuccess,
  MakePaymentFailed,
  MakePaymentSuccess,
  OnGatewayTransactionError,
  OnGatewayTransactionSuccess,
  OnRetryGatewayTransaction
} from 'src/app/store/payment/payment.action';
import { PaymentState } from 'src/app/store/payment/payment.state';
import { LastGatewayTransaction } from 'src/app/store/payment/payment.state.model';

@Component({
  selector: 'pos-gateway-transaction',
  templateUrl: './gateway-transaction.modal.html',
  styleUrls: ['./gateway-transaction.modal.scss']
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class GatewayTransactionModal extends ReactiveComponent {
  @Select(PaymentState.isGatewayTransactionTerminalStripe)
  readonly showCancelTransactionButton$: Observable<boolean>;
  @Select(PaymentState.lastGatewayTransaction)
  readonly lastGatewayTransaction$: Observable<LastGatewayTransaction>;

  view: GatewayTransactionModalView = GatewayTransactionModalView.IN_PROGRESS;
  gatewayError: { message: string; cmdStatus?: string } = null;
  // Amount paid toward the ticket, excluding tip.
  paymentAmount = 0;
  // Gratuity collected on the card terminal. Always displayed, including $0.00.
  tipAmount = 0;

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly actions$: Actions,
    private readonly store: Store
  ) {
    super();
  }

  *onSubscribe() {
    yield this.actions$
      .pipe(ofActionSuccessful(OnGatewayTransactionSuccess))
      .subscribe(() => (this.view = GatewayTransactionModalView.SUCCESS));

    yield this.actions$
      .pipe(ofActionSuccessful(MakePaymentSuccess))
      .subscribe(({ payment }: MakePaymentSuccess) => {
        this.paymentAmount = payment.transaction?.amount || 0;
        this.tipAmount = payment.tip_amount || 0;
      });

    yield this.actions$
      .pipe(
        ofActionSuccessful(
          MakePaymentFailed, // DEF-5964 MakePaymentFailed is the wrapper of MakePaymentError which is missing in the code
          OnGatewayTransactionError,
          CancelGatewayTransactionSuccess
        )
      )
      .subscribe((action) => {
        this.view = GatewayTransactionModalView.ERROR;
        this.gatewayError =
          'gatewayError' in action
            ? { message: action.gatewayError, cmdStatus: action.cmdStatus }
            : null;
      });
  }

  get totalCharged(): number {
    // Money math goes through decimal.js repo-wide (see GeneralHelper.asDecimal)
    // to avoid float rounding artifacts on this customer-visible total.
    return GeneralHelper.asDecimal(this.paymentAmount + this.tipAmount);
  }

  onRetry() {
    this.store.dispatch(new OnRetryGatewayTransaction());
    this.view = GatewayTransactionModalView.IN_PROGRESS;
  }

  onCancelTransaction() {
    this.store.dispatch(new CancelGatewayTransaction());
  }

  /**
   * Dismisses the modal.
   *
   * @returns A promise that resolves when the modal is dismissed.
   */
  async onModalDismiss() {
    await this.modalCtrl.dismiss();
  }
}
